import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Loader } from "rsuite";

import Input from "../Inputs/Input";
import { InputOtpCode } from "../Inputs";
import AccountEditModal from "./AccountEditModal";
import API from "../../utils/api";
import { useModal } from "../../context/modal-context";

const CODE_LENGTH = 6;

const formatTime = (date) =>
  new Date(date).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });

const getErrorMessage = (error, fallback) =>
  error.response?.data?.error?.message || fallback;

/**
 * ContactChangeModal - Modification d'un contact (email / téléphone) avec validation OTP
 *
 * Étapes :
 * 1. Saisie de la nouvelle valeur → POST /prescriber/contact/request-change
 *    (un code est envoyé au NOUVEAU contact)
 * 2. Saisie du code reçu → POST /prescriber/contact/confirm-change
 *    (le contact n'est modifié qu'après validation du code)
 *
 * Props:
 *   type         — "email" | "phone"
 *   icon         — média { path, alt } de l'en-tête
 *   title        — titre de la modale
 *   currentLabel — libellé de la valeur actuelle
 *   currentValue — valeur actuelle
 *   newLabel     — libellé du champ de saisie
 *   placeholder  — placeholder du champ de saisie
 *   inputType    — type HTML du champ de saisie
 *   inputProps   — props HTML additionnelles du champ (inputMode, autoComplete…)
 *   sanitize     — (value) => value, filtre les caractères saisis ou collés dans le champ
 *   rules        — règles react-hook-form du champ de saisie
 *   isValid      — (value) => boolean, active le bouton "Valider" de l'étape 1
 *   onSuccess    — callback(profile) après validation du code
 */
export default function ContactChangeModal({
  type,
  icon,
  title,
  currentLabel,
  currentValue,
  newLabel,
  placeholder,
  inputType = "text",
  inputProps,
  sanitize,
  rules,
  isValid,
  onSuccess,
}) {
  const { hideModal } = useModal();
  const [step, setStep] = useState("input"); // "input" | "code"
  const [loading, setLoading] = useState(false);
  const [loadingResend, setLoadingResend] = useState(false);
  const [pendingValue, setPendingValue] = useState("");
  const [expiresAt, setExpiresAt] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [resendMessage, setResendMessage] = useState(null);

  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors },
  } = useForm({ shouldUnregister: true, mode: "onTouched" });

  const newValue = watch("newValue", "");
  const newValueField = register("newValue", rules);
  const code = watch("code", "");

  // Délai avant de pouvoir redemander un code
  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  /**
   * Demande l'envoi d'un code au nouveau contact
   */
  const requestCode = async (value) => {
    const response = await API.post(
      "/prescriber/contact/request-change",
      { type, value },
      { silent: true }
    );
    const { newValue: normalizedValue, expiresAt, resendDelaySeconds } = response.data.data;
    setPendingValue(normalizedValue);
    setExpiresAt(expiresAt);
    setCountdown(resendDelaySeconds);
  };

  /**
   * Étape 1 : envoi du code
   */
  const onSubmitValue = async (data) => {
    try {
      setLoading(true);
      await requestCode(data.newValue);
      setResendMessage(null);
      setStep("code");
    } catch (error) {
      const remainingSeconds = error.response?.data?.error?.remainingSeconds;
      if (remainingSeconds) setCountdown(remainingSeconds);
      setError("newValue", {
        message: getErrorMessage(error, "Erreur lors de l'envoi du code de vérification."),
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Étape 2 : validation du code et application de la modification
   */
  const onSubmitCode = async (data) => {
    try {
      setLoading(true);
      const response = await API.post(
        "/prescriber/contact/confirm-change",
        { type, code: data.code },
        { silent: true }
      );
      hideModal();
      onSuccess?.(response.data.data);
    } catch (error) {
      const { remainingAttempts } = error.response?.data?.error || {};
      const message = getErrorMessage(error, "Erreur lors de la vérification du code.");
      setError("code", {
        message: remainingAttempts
          ? `${message} Il vous reste ${remainingAttempts} essai${remainingAttempts > 1 ? "s" : ""}.`
          : message,
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Renvoi d'un nouveau code au contact en attente de validation
   */
  const handleResendCode = async () => {
    try {
      setLoadingResend(true);
      setResendMessage(null);
      await requestCode(pendingValue);
      setResendMessage("Un nouveau code vient de vous être envoyé.");
    } catch (error) {
      const remainingSeconds = error.response?.data?.error?.remainingSeconds;
      if (remainingSeconds) setCountdown(remainingSeconds);
      setError("code", {
        message: getErrorMessage(error, "Erreur lors de l'envoi du code de vérification."),
      });
    } finally {
      setLoadingResend(false);
    }
  };

  if (step === "code") {
    return (
      <AccountEditModal
        icon={icon}
        title={title}
        onSubmit={handleSubmit(onSubmitCode)}
        submitDisabled={code.trim().length !== CODE_LENGTH}
        loading={loading}
      >
        <p className="text-sm leading-[22px] text-neutral-dark">
          Afin de valider cette modification, nous venons de vous envoyer un code
          de vérification {type === "email" ? "par mail à l'adresse " : "par SMS au "}
          <strong className="font-bold break-all">{pendingValue}</strong>.
          {expiresAt && (
            <>
              {" "}Ce code est valide jusqu'à{" "}
              <strong className="font-bold">{formatTime(expiresAt)}</strong>.
            </>
          )}
        </p>

        <div>
          <label className="text-sm font-semibold text-neutral-dark">
            Code de vérification{" "}
            <span className="font-normal">({CODE_LENGTH} chiffres)</span>
          </label>
          <InputOtpCode
            {...register("code", {
              required: "Le code est requis",
              pattern: {
                value: /^\d{6}$/,
                message: `Le code doit contenir ${CODE_LENGTH} chiffres`,
              },
            })}
            id="code"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            maxLength={CODE_LENGTH}
            className="!w-fit min-w-[262px] !h-[30px]"
            error={errors.code}
          />
        </div>

        <div className="flex flex-col gap-1 text-sm text-neutral-dark">
          <span>
            Vous n'avez rien reçu ?{" "}
            <button
              type="button"
              onClick={handleResendCode}
              disabled={countdown > 0 || loadingResend}
              className="font-semibold underline text-primary hover:opacity-70 disabled:opacity-60 disabled:no-underline disabled:cursor-not-allowed"
            >
              {loadingResend ? (
                <Loader size="xs" />
              ) : (
                `Recevoir un nouveau code${countdown > 0 ? ` (${countdown} s)` : ""}`
              )}
            </button>
          </span>
          {type === "email" && (
            <span className="text-xs italic text-neutral-medium">
              Pensez à vérifier vos messages indésirables.
            </span>
          )}
          {resendMessage && <span className="text-xs text-neutral-medium">{resendMessage}</span>}
          <button
            type="button"
            onClick={() => setStep("input")}
            className="w-fit font-semibold underline text-primary hover:opacity-70"
          >
            Modifier {type === "email" ? "l'adresse saisie" : "le numéro saisi"}
          </button>
        </div>
      </AccountEditModal>
    );
  }

  return (
    <AccountEditModal
      icon={icon}
      title={title}
      onSubmit={handleSubmit(onSubmitValue)}
      submitDisabled={!isValid(newValue)}
      loading={loading}
    >
      {/* Valeur actuelle */}
      <div className="flex flex-col space-y-1">
        <label className="text-base font-semibold text-neutral-dark">
          {currentLabel}
        </label>
        <p className="text-base text-neutral-dark break-all">{currentValue || "- -"}</p>
      </div>

      {/* Nouvelle valeur */}
      <div>
        <label className="text-sm font-semibold text-neutral-dark">{newLabel}</label>
        <Input
          {...newValueField}
          {...inputProps}
          onChange={(event) => {
            if (sanitize) event.target.value = sanitize(event.target.value);
            newValueField.onChange(event);
          }}
          id="newValue"
          defaultValue={pendingValue}
          placeholder={placeholder}
          className="!w-fit min-w-[262px]"
          type={inputType}
          error={errors.newValue}
        />
        <p className="pt-1 text-xs italic text-neutral-medium">
          Un code de vérification sera envoyé {type === "email" ? "à cette adresse" : "à ce numéro"} pour
          confirmer la modification.
        </p>
      </div>
    </AccountEditModal>
  );
}
